import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../../../store/admin/vehiclesSocketStore";
import {
  useDeviceCommandsStore,
  getCommandStatusLabel,
  COMMAND_STATUS_COLORS,
  type SocketCommand,
} from "../../../../../../../store/admin/deviceCommandsStore";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

interface CommandsHistoryModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
  onCommandClick: (command: SocketCommand) => void;
  /** Shown when e.g. the HTTP client timed out but the command may still be processing */
  noticeMessage?: string | null;
}

export default function CommandsHistoryModal({
  vehicle,
  isDarkMode,
  onClose,
  onCommandClick,
  noticeMessage,
}: CommandsHistoryModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const [currentPage, setCurrentPage] = useState(1);

  // Obtener comandos del socket para este vehículo
  const commandsByDevice = useDeviceCommandsStore((state) => state.commandsByDevice);
  const socketCommands = useMemo<SocketCommand[]>(() => {
    if (!vehicle?.id) return [];
    return commandsByDevice[vehicle.id] || [];
  }, [commandsByDevice, vehicle?.id]);

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const headerBg = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const rowHoverBg = isDarkMode ? "hover:bg-[#252525]" : "hover:bg-gray-50";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const ITEMS_PER_PAGE = 10;

  // Paginación local
  const totalItems = socketCommands.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const paginatedCommands = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return socketCommands.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [socketCommands, currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Formatear timestamp a fecha/hora local
  const formatCommandTime = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatCommandDate = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Obtener color del estado
  const getStatusColor = (status: SocketCommand["status"]) => {
    const colors = COMMAND_STATUS_COLORS[status];
    if (!colors) return { bg: "bg-gray-100", text: "text-gray-700" };
    return isDarkMode
      ? { bg: colors.darkBg, text: colors.darkText }
      : { bg: colors.bg, text: colors.text };
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden border ${borderColor} shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-terminal-box-line text-black text-lg"></i>
            </div>
            <div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("commandsHistoryModal.title")}
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                {vehicle.vehicle.name} • {t("commandsHistoryModal.commandsCount", { count: totalItems })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            } rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {noticeMessage ? (
          <div
            className={`mx-4 my-2 flex gap-2 rounded-lg border px-3 py-3 text-sm ${
              isDarkMode
                ? "border-amber-700/60 bg-amber-950/40 text-amber-100"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            <i className="ri-time-line shrink-0 text-lg text-amber-500" aria-hidden />
            <p className="leading-snug">{noticeMessage}</p>
          </div>
        ) : null}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {socketCommands.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i
                className={`ri-terminal-box-line text-4xl ${textSecondary} mb-3`}
              ></i>
              <p className={textSecondary}>
                {t("commandsHistoryModal.noCommands")}
              </p>
              <p className={`text-xs ${textSecondary} mt-1`}>
                {t("commandsHistoryModal.commandsWillAppear")}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className={`${headerBg} sticky top-0`}>
                <tr>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("commandsHistoryModal.command")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("commandsHistoryModal.dateTime")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("commandsHistoryModal.status")}
                  </th>
                  <th
                    className={`text-left py-3 px-4 font-medium ${textSecondary} text-sm`}
                  >
                    {t("commandsHistoryModal.details")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCommands.map((command, index) => {
                  const statusColor = getStatusColor(command.status);
                  return (
                    <tr
                      key={command.id}
                      onClick={() => onCommandClick(command)}
                      className={`${rowHoverBg} transition-colors cursor-pointer ${
                        index % 2 === 1
                          ? isDarkMode
                            ? "bg-[#1f1f1f]"
                            : "bg-gray-50/50"
                          : ""
                      }`}
                    >
                      <td
                        className={`py-4 px-4 ${textColor} text-sm font-medium`}
                      >
                        <div className="flex items-center gap-2">
                          <i className="ri-terminal-line text-primary-500"></i>
                          {command.command_name || command.command_code}
                        </div>
                      </td>
                      <td className={`py-4 px-4 ${textColor} text-sm`}>
                        <div>
                          <span>{formatCommandDate(command.receivedAt)}</span>
                          <br />
                          <span className={textSecondary}>
                            {formatCommandTime(command.receivedAt)}
                          </span>
                        </div>
                      </td>
                      <td className={`py-4 px-4 text-sm`}>
                        <span
                          className={`px-2 py-1 rounded text-xs ${statusColor.bg} ${statusColor.text}`}
                        >
                          {getCommandStatusLabel(command.status)}
                        </span>
                      </td>
                      <td className={`py-4 px-4 ${textSecondary} text-sm`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onCommandClick(command);
                          }}
                          className={`p-1.5 rounded-lg border ${borderColor} ${
                            isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          } transition-colors`}
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer con paginación */}
        <div
          className={`p-4 border-t ${borderColor} flex items-center justify-between`}
        >
          {/* Espacio vacío o botón futuro */}
          <div></div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                className={`p-2 rounded-lg border ${borderColor} ${
                  currentPage <= 1
                    ? "opacity-50 cursor-not-allowed"
                    : isDarkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-100"
                } transition-colors`}
              >
                <i className={`ri-arrow-left-s-line ${textSecondary}`}></i>
              </button>

              <span className={`text-sm ${textSecondary}`}>
                {t("commandsHistoryModal.page")} {currentPage} {t("commandsHistoryModal.of")} {totalPages}
              </span>

              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className={`p-2 rounded-lg border ${borderColor} ${
                  currentPage >= totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : isDarkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-100"
                } transition-colors`}
              >
                <i className={`ri-arrow-right-s-line ${textSecondary}`}></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
